
import { type ReactNode } from 'react'
import { LoaderCircle } from 'lucide-react'
import { useMobileDetect } from '@/hooks/useMediaQuery'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'

type Props = {
  title?: string
  children: ReactNode
  description?: string
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  isEdit?: boolean
  onSubmit?: () => void
  onCancel?: () => void
  isSubmitting?: boolean
  submitLabel?: string
  isDraggable?: boolean
  /** 送信ボタンだけを押せなくする（依頼を受け付けていない相手など） */
  isSubmitDisabled?: boolean
  /** 独自の閉じるボタンを持つモーダルでアイコンの重複を避けるために使う */
  hideCloseButton?: boolean
  /** ヘッダーを表示せず、title をスクリーンリーダー向けにのみ出力する */
  hideHeader?: boolean
}

export function ProfessionalModal({
  title,
  children,
  description = '',
  isOpen = false,
  isEdit = false,
  onSubmit,
  onOpenChange,
  isSubmitting,
  submitLabel = '保存',
  isDraggable = true,
  hideCloseButton = false,
  hideHeader = false,
  isSubmitDisabled = false,
}: Props) {
  const isMobile = useMobileDetect()
  const showHeader = !hideHeader && (title || description)

  if (!isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        {/* <DialogTrigger asChild>{trigger}</DialogTrigger> */}
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          hideCloseButton={hideCloseButton}
        >
          {hideHeader ? (
            // Radix の a11y 要件を満たすため、非表示でも DialogTitle は出力する
            <DialogTitle className="sr-only">{title || ''}</DialogTitle>
          ) : (
            <DialogHeader>
              <DialogTitle className="pr-5 line-clamp-1">{title || ''}</DialogTitle>
              <DialogDescription>{description || ''}</DialogDescription>
            </DialogHeader>
          )}
          {children}
          <DialogFooter>
            <DialogClose asChild>
              <Button
                className="w-40"
                variant="outline"
                disabled={isSubmitting}
              >
                {isEdit ? 'キャンセル' : '閉じる'}
              </Button>
            </DialogClose>
            {isEdit && (
              <Button
                className="w-40"
                variant="primary"
                onClick={onSubmit}
                disabled={isSubmitting || isSubmitDisabled}
              >
                {isSubmitting ? (
                  <LoaderCircle className="animate-spin text-sm text-white" />
                ) : (
                  <span>{submitLabel}</span>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer open={isOpen}
      onOpenChange={onOpenChange}
      handleOnly={!isDraggable}
      repositionInputs={false}
    >
      {/* <DrawerTrigger asChild>{trigger}</DrawerTrigger> */}
      {/* hideHeader のモーダルは独自のヘッダーを上端から描画するため、ドラッグハンドルも出さない */}
      <DrawerContent className="h-full max-h-[90vh]" hideHandle={hideHeader}>
        {hideHeader && (
          // Radix(vaul) の a11y 要件を満たすため、非表示でも DrawerTitle は出力する
          <DrawerTitle className="sr-only">{title || ''}</DrawerTitle>
        )}
        {showHeader && (
          <DrawerHeader className="text-left">
            {title && <DrawerTitle>{title}</DrawerTitle>}
            {description && (
              <DrawerDescription>{description}</DrawerDescription>
            )}
          </DrawerHeader>
        )}
        <div className="overflow-y-auto">
          <div className="container my-4 flex-1">{children}</div>
          <DrawerFooter>
            <DrawerClose asChild className="flex-1">
              <Button variant="outline" disabled={isSubmitting}>
                {isEdit ? 'キャンセル' : '閉じる'}
              </Button>
            </DrawerClose>
            {isEdit && (
              <Button
                variant="primary"
                disabled={isSubmitting || isSubmitDisabled}
                onClick={onSubmit}
                className="flex-1"
              >
                {isSubmitting ? (
                  <LoaderCircle className="animate-spin text-xs text-white" />
                ) : (
                  <span>{submitLabel}</span>
                )}
              </Button>
            )}
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
